import React from 'react';
import { createTheme, ThemeProvider, responsiveFontSizes } from '@mui/material/styles';

/**
 * Shop design system — premium apparel-retail look (Jockey-style).
 *
 * Design tokens shared by every shop page EXCEPT the storefront home:
 *  - Sharp edges (no border radius), hairline #e5e5e5 borders instead of shadows
 *  - Black primary CTAs, red accent reserved for offers/sale
 *  - Uppercase, letter-spaced buttons/tabs/overlines
 *
 * Apply via <ShopTheme> (see StorefrontLayout `themed` prop).
 */

export const SHOP_COLORS = {
  ink: '#1a1a1a',
  inkHover: '#3d3d3d',
  accent: '#c8102e', // offer / sale red
  hairline: '#e5e5e5',
  surface: '#ffffff',
  surfaceMuted: '#f7f7f7',
  textSecondary: '#6b6b6b',
};

const FONT_STACK = [
  'Inter',
  '"Helvetica Neue"',
  'Helvetica',
  'Arial',
  'sans-serif',
].join(',');

let shopTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: SHOP_COLORS.ink, contrastText: '#ffffff' },
    secondary: { main: SHOP_COLORS.accent },
    error: { main: SHOP_COLORS.accent },
    success: { main: '#0a7d33' },
    background: { default: SHOP_COLORS.surface, paper: SHOP_COLORS.surface },
    divider: SHOP_COLORS.hairline,
    text: { primary: SHOP_COLORS.ink, secondary: SHOP_COLORS.textSecondary },
    grey: { 50: '#fafafa', 100: SHOP_COLORS.surfaceMuted, 200: '#ededed' },
  },
  shape: { borderRadius: 0 },
  typography: {
    fontFamily: FONT_STACK,
    h3: { fontWeight: 800, letterSpacing: '0.02em' },
    h4: { fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase' },
    h5: { fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' },
    h6: { fontWeight: 700, letterSpacing: '0.02em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 700, letterSpacing: '0.04em' },
    overline: { fontWeight: 700, letterSpacing: '0.14em' },
    button: { fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 0 },
        sizeLarge: { paddingTop: 12, paddingBottom: 12 },
        containedPrimary: {
          backgroundColor: SHOP_COLORS.ink,
          '&:hover': { backgroundColor: SHOP_COLORS.inkHover },
        },
        outlined: {
          borderColor: SHOP_COLORS.ink,
          color: SHOP_COLORS.ink,
          '&:hover': {
            borderColor: SHOP_COLORS.ink,
            backgroundColor: 'rgba(26,26,26,0.04)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 0 },
        outlined: { borderColor: SHOP_COLORS.hairline },
      },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 0 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 0, fontWeight: 700, letterSpacing: '0.06em' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: SHOP_COLORS.ink,
            borderWidth: 1,
          },
        },
        notchedOutline: { borderColor: '#cfcfcf' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { '&.Mui-focused': { color: SHOP_COLORS.ink } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          '&.Mui-selected': { color: SHOP_COLORS.ink },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: SHOP_COLORS.ink, height: 2 },
      },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          borderTop: `1px solid ${SHOP_COLORS.hairline}`,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: { fontWeight: 600 },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { '&.Mui-checked': { color: SHOP_COLORS.ink } },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { '&.Mui-checked': { color: SHOP_COLORS.ink } },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: SHOP_COLORS.ink },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: SHOP_COLORS.ink },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: { color: SHOP_COLORS.ink },
        thumb: { borderRadius: 0, width: 14, height: 14 },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontWeight: 600,
          '&.Mui-selected': {
            backgroundColor: SHOP_COLORS.ink,
            color: '#fff',
            '&:hover': { backgroundColor: SHOP_COLORS.inkHover },
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        },
        li: { fontSize: '0.75rem' },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { color: 'inherit' } },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-active': { color: SHOP_COLORS.ink },
          '&.Mui-completed': { color: SHOP_COLORS.ink },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.7rem',
          fontWeight: 600,
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 0 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 0 } },
    },
    MuiSkeleton: {
      styleOverrides: { rectangular: { borderRadius: 0 } },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: '0.72rem',
          backgroundColor: SHOP_COLORS.surfaceMuted,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 0, backgroundColor: SHOP_COLORS.hairline },
        bar: { backgroundColor: SHOP_COLORS.ink },
      },
    },
  },
});

shopTheme = responsiveFontSizes(shopTheme);

export { shopTheme };

/** Scoped theme wrapper for shop pages (everything except the storefront home). */
const ShopTheme = ({ children }) => (
  <ThemeProvider theme={shopTheme}>{children}</ThemeProvider>
);

export default ShopTheme;
